/**
 * WCAG 1.4.3 AA (4.5:1) + tokens alinhados a AAA 1.4.6 (7:1) para texto/link em fundo claro.
 * Fonte única de verdade para hex usados no Portal (styled, global, LESS, inline).
 */

function parseColor(color) {
  if (!color || typeof color !== 'string') {
    return null;
  }

  const value = color.trim();

  if (value.startsWith('#')) {
    let hex = value.slice(1);
    if (hex.length === 3) {
      hex = hex
        .split('')
        .map(char => `${char}${char}`)
        .join('');
    }
    if (hex.length !== 6 || Number.isNaN(Number.parseInt(hex, 16))) {
      return null;
    }
    return {
      r: Number.parseInt(hex.slice(0, 2), 16),
      g: Number.parseInt(hex.slice(2, 4), 16),
      b: Number.parseInt(hex.slice(4, 6), 16),
    };
  }

  const rgbMatch = value.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (rgbMatch) {
    return {
      r: Number(rgbMatch[1]),
      g: Number(rgbMatch[2]),
      b: Number(rgbMatch[3]),
    };
  }

  return null;
}

function toHex({ r, g, b }) {
  return `#${[r, g, b]
    .map(channel =>
      Math.max(0, Math.min(255, Math.round(channel)))
        .toString(16)
        .padStart(2, '0')
    )
    .join('')}`;
}

function channelLuminance(channel) {
  const value = channel / 255;
  return value <= 0.03928
    ? value / 12.92
    : ((value + 0.055) / 1.055) ** 2.4;
}

function relativeLuminance(rgb) {
  return (
    0.2126 * channelLuminance(rgb.r) +
    0.7152 * channelLuminance(rgb.g) +
    0.0722 * channelLuminance(rgb.b)
  );
}

export function getContrastRatio(foreground, background) {
  const fg = parseColor(foreground);
  const bg = parseColor(background);
  if (!fg || !bg) {
    return 0;
  }

  const lighter = Math.max(relativeLuminance(fg), relativeLuminance(bg));
  const darker = Math.min(relativeLuminance(fg), relativeLuminance(bg));
  return (lighter + 0.05) / (darker + 0.05);
}

/** Texto primário AA/AAA em fundo claro. */
export const AA_TEXT_PRIMARY_ON_LIGHT = '#262626';
/**
 * Texto secundário / placeholder — AAA (~9:1 em #f5f5f5; antes #595959 ~6,4:1).
 * AccessMonitor textC_02 (1.4.6) exige 7:1 para texto normal.
 */
export const AA_TEXT_ON_LIGHT = '#434343';
/** Links AAA em fundo claro (antes #0050b3 ~6,9:1 em #f5f5f5). */
export const AA_LINK_ON_LIGHT = '#003a8c';
export const AA_LINK_HOVER_ON_LIGHT = '#002766';
/** Superfície / card opaco (evita texto sobre foto). */
export const AA_SURFACE = '#ffffff';
/** Disabled com contraste legível; WCAG 1.4.3 isenta UI inativa, mas AM textC_02 conta. */
export const AA_DISABLED_TEXT = '#434343';
export const AA_DISABLED_BG = '#f5f5f5';
export const AA_DISABLED_BORDER = '#d9d9d9';
/** Tags Ant Design success/error com texto AA. */
export const AA_TAG_SUCCESS_TEXT = '#135200';
export const AA_TAG_SUCCESS_BG = '#f6ffed';
export const AA_TAG_SUCCESS_BORDER = '#389e0d';
export const AA_TAG_ERROR_TEXT = '#a8071a';
export const AA_TAG_ERROR_BG = '#fff2f0';
export const AA_TAG_ERROR_BORDER = '#cf1322';

/**
 * Tokens para ThemeProvider / styled-components (`theme.a11y`).
 */
export const a11yTokens = {
  textPrimary: AA_TEXT_PRIMARY_ON_LIGHT,
  textSecondary: AA_TEXT_ON_LIGHT,
  link: AA_LINK_ON_LIGHT,
  linkHover: AA_LINK_HOVER_ON_LIGHT,
  surface: AA_SURFACE,
  disabledText: AA_DISABLED_TEXT,
  disabledBg: AA_DISABLED_BG,
  disabledBorder: AA_DISABLED_BORDER,
  tagSuccessText: AA_TAG_SUCCESS_TEXT,
  tagSuccessBg: AA_TAG_SUCCESS_BG,
  tagSuccessBorder: AA_TAG_SUCCESS_BORDER,
  tagErrorText: AA_TAG_ERROR_TEXT,
  tagErrorBg: AA_TAG_ERROR_BG,
  tagErrorBorder: AA_TAG_ERROR_BORDER,
};

/**
 * Cor de link acessível: usa corFonte do portal se válida, senão token de link AA.
 */
export function resolveLinkColor(corFonte) {
  if (corFonte && typeof corFonte === 'string' && corFonte.trim()) {
    return corFonte;
  }
  return AA_LINK_ON_LIGHT;
}

/**
 * Cor de texto primário: corFonte ou token AA.
 */
export function resolveTextColor(corFonte) {
  if (corFonte && typeof corFonte === 'string' && corFonte.trim()) {
    return corFonte;
  }
  return AA_TEXT_PRIMARY_ON_LIGHT;
}

/**
 * Garante contraste mínimo da foreground contra background (padrão AA 4.5).
 * Se a cor for inválida, retorna fallback seguro para fundo claro.
 */
export function ensureContrast(
  foreground,
  background = AA_SURFACE,
  minRatio = 4.5,
  fallback = AA_TEXT_PRIMARY_ON_LIGHT
) {
  const fg = parseColor(foreground);
  const bg = parseColor(background);

  if (!bg) {
    return foreground || fallback;
  }

  if (!fg) {
    return fallback;
  }

  if (getContrastRatio(toHex(fg), toHex(bg)) >= minRatio) {
    return toHex(fg);
  }

  const shouldDarken = relativeLuminance(bg) > 0.5;
  let current = { ...fg };

  for (let step = 0; step < 60; step += 1) {
    if (getContrastRatio(toHex(current), toHex(bg)) >= minRatio) {
      return toHex(current);
    }

    if (shouldDarken) {
      current = {
        r: Math.max(0, current.r - 6),
        g: Math.max(0, current.g - 6),
        b: Math.max(0, current.b - 6),
      };
    } else {
      current = {
        r: Math.min(255, current.r + 6),
        g: Math.min(255, current.g + 6),
        b: Math.min(255, current.b + 6),
      };
    }
  }

  return shouldDarken ? fallback : AA_SURFACE;
}

/**
 * Escurece o background até o foreground fixo (ex.: #fff do menu) atingir AA.
 * Usado no sider / botões primary com texto branco.
 */
export function ensureBackgroundForForeground(
  background,
  foreground = '#ffffff',
  minRatio = 4.5,
  fallback = '#1a1a1a'
) {
  const fg = parseColor(foreground);
  let bg = parseColor(background);

  if (!fg) {
    return background || fallback;
  }
  if (!bg) {
    return fallback;
  }

  if (getContrastRatio(toHex(fg), toHex(bg)) >= minRatio) {
    return toHex(bg);
  }

  let current = { ...bg };
  for (let step = 0; step < 80; step += 1) {
    if (getContrastRatio(toHex(fg), toHex(current)) >= minRatio) {
      return toHex(current);
    }
    current = {
      r: Math.max(0, current.r - 5),
      g: Math.max(0, current.g - 5),
      b: Math.max(0, current.b - 5),
    };
  }

  return fallback;
}
